import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { formatBookingDate, formatDate } from '../utils/dateFormat';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Clock, Plus, Send, User, Loader2 } from 'lucide-react';

function AdminBookings() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [sendingReminders, setSendingReminders] = useState(false);
  
  // Add booking dialog state
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addingBooking, setAddingBooking] = useState(false);
  const [newBooking, setNewBooking] = useState({
    participantEmail: '',
    scheduledAt: '',
    duration: 60,
    sessionType: 'facilitated_session',
    notes: ''
  });
  
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    
    if (user && user.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    
    if (user) {
      fetchBookings();
      fetchStats();
    }
  }, [user, authLoading]);
  
  const fetchBookings = async () => {
    try {
      const response = await axios.get('/api/admin/bookings');
      setBookings(response.data.bookings || []);
    } catch (error) {
      toast.error('Failed to fetch bookings');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/admin/bookings/stats');
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };
  
  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const response = await axios.post('/api/bookings/send-reminders');
      toast.success(`Sent ${response.data.count} reminder(s)`);
    } catch (error) {
      toast.error('Failed to send reminders');
    } finally {
      setSendingReminders(false);
    }
  };
  
  const handleAddBooking = async () => {
    if (!newBooking.participantEmail || !newBooking.scheduledAt) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setAddingBooking(true);
    try {
      // Convert local datetime to ISO format
      const scheduledAt = new Date(newBooking.scheduledAt).toISOString();
      
      await axios.post('/api/admin/bookings', {
        ...newBooking,
        scheduledAt
      });
      
      toast.success('Booking created successfully');
      setShowAddDialog(false);
      setNewBooking({
        participantEmail: '',
        scheduledAt: '',
        duration: 60,
        sessionType: 'facilitated_session',
        notes: ''
      });
      fetchBookings();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setAddingBooking(false);
    }
  };
  
  const handleUpdateBooking = async (bookingId, updates) => {
    try {
      await axios.put(`/api/admin/bookings/${bookingId}`, updates);
      toast.success('Booking updated');
      fetchBookings();
      setSelectedBooking(null);
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };
  
  const getStatusBadge = (status) => {
    const styles = {
      confirmed: 'bg-emerald-100 text-emerald-700',
      completed: 'bg-blue-100 text-blue-700',
      cancelled: 'bg-red-100 text-red-700',
      pending: 'bg-amber-100 text-amber-700',
      pending_approval: 'bg-amber-100 text-amber-700',
      rejected: 'bg-red-100 text-red-700',
      no_show: 'bg-gray-100 text-gray-700'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {(status || 'unknown').replace(/_/g, ' ').toUpperCase()}
      </span>
    );
  };
  
  const filteredBookings = filterStatus === 'all' 
    ? bookings 
    : bookings.filter(b => b.status === filterStatus);
  
  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div data-testid="bookings-page">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-2xl font-semibold flex items-center gap-2">
            <Calendar className="w-6 h-6 text-primary" />
            Booking Management
          </h1>
          <p className="text-muted-foreground mt-1">Manage facilitated session bookings</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={handleSendReminders}
            disabled={sendingReminders}
          >
            {sendingReminders ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Send Reminders
          </Button>
          <Button onClick={() => setShowAddDialog(true)} data-testid="add-booking-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Booking
          </Button>
        </div>
      </div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-8"
      >
          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-card border border-border rounded-[var(--radius)] p-4">
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-2xl font-semibold">{stats.total}</p>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-[var(--radius)] p-4">
                <p className="text-sm text-emerald-600">Upcoming</p>
                <p className="text-2xl font-semibold text-emerald-700">{stats.upcoming}</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-[var(--radius)] p-4">
                <p className="text-sm text-blue-600">Confirmed</p>
                <p className="text-2xl font-semibold text-blue-700">{stats.confirmed}</p>
              </div>
              <div className="bg-violet-50 border border-violet-200 rounded-[var(--radius)] p-4">
                <p className="text-sm text-violet-600">Completed</p>
                <p className="text-2xl font-semibold text-violet-700">{stats.completed}</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-[var(--radius)] p-4">
                <p className="text-sm text-red-600">Cancelled</p>
                <p className="text-2xl font-semibold text-red-700">{stats.cancelled}</p>
              </div>
            </div>
          )}
          
          {/* Actions Bar */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Filter status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Bookings</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="pending_approval">Pending Approval</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="no_show">No Show</SelectItem>
                </SelectContent>
              </Select>
              
              <span className="text-sm text-muted-foreground">
                {filteredBookings.length} booking(s)
              </span>
            </div>
            
            <Button 
              onClick={handleSendReminders}
              disabled={sendingReminders}
              variant="outline"
            >
              {sendingReminders ? 'Sending...' : 'Send Pending Reminders'}
            </Button>
          </div>
          
          {/* Bookings Table */}
          <div className="bg-card border border-border rounded-[var(--radius)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Participant</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Scheduled</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Status</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Assessments</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                        No bookings found
                      </td>
                    </tr>
                  ) : (
                    filteredBookings.map((booking) => (
                      <tr key={booking.id} className="border-b border-border last:border-0 hover:bg-muted/30">
                        <td className="px-4 py-4">
                          <div>
                            <p className="font-medium">
                              {booking.participant?.firstName || booking.lunacalData?.attendees?.[0]?.name || ''} {booking.participant?.lastName || ''}
                            </p>
                            <p className="text-sm text-muted-foreground">{booking.participant?.email || booking.lunacalData?.attendees?.[0]?.email || 'Unknown'}</p>
                            {booking.eventTitle && (
                              <p className="text-xs text-primary mt-1">{booking.eventTitle}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <p className="font-medium">{formatBookingDate(booking.scheduledAt)}</p>
                          <p className="text-sm text-muted-foreground">{booking.duration} min</p>
                        </td>
                        <td className="px-4 py-4">
                          {getStatusBadge(booking.status)}
                          {booking.reminderSent && (
                            <span className="ml-2 text-xs text-muted-foreground">📬</span>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <div className="space-y-1">
                            {booking.assessmentSummary?.map((a, idx) => (
                              <div key={idx} className="text-xs">
                                {a.type === 'fear' && (
                                  <span className="text-amber-600">
                                    EDGE: {a.primaryFear} ({a.intensity})
                                  </span>
                                )}
                                {a.type === 'archetype' && (
                                  <span className="text-violet-600">
                                    CORE: {a.primaryArchetype}
                                    {a.hasGap && ' ⚠️'}
                                  </span>
                                )}
                              </div>
                            ))}
                            {(!booking.assessmentSummary || booking.assessmentSummary.length === 0) && (
                              <span className="text-xs text-muted-foreground">No diagnostics</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSelectedBooking(booking)}
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Webhook Info */}
          <div className="bg-muted/30 border border-border rounded-[var(--radius)] p-6">
            <h3 className="font-semibold mb-2">Lunacal Webhook URL</h3>
            <p className="text-sm text-muted-foreground mb-3">
              Configure this URL in your Lunacal event settings (Advanced &rarr; Webhooks) to receive booking events automatically:
            </p>
            <code className="block bg-card p-3 rounded text-sm break-all">
              {window.location.origin}/api/webhooks/lunacal
            </code>
            <p className="text-xs text-muted-foreground mt-2">
              Supports all events: created, cancelled, rescheduled, requested, rejected, payment initiated, payment completed.
            </p>
          </div>
        </motion.div>
      
      {/* Booking Detail Modal */}
      <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Booking Details</DialogTitle>
            <DialogDescription>
              View and manage booking information
            </DialogDescription>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-6 mt-4">
              {/* Participant Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground">Participant</label>
                  <p className="font-medium">
                    {selectedBooking.participant?.firstName} {selectedBooking.participant?.lastName}
                  </p>
                  <p className="text-sm">{selectedBooking.participant?.email}</p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Scheduled</label>
                  <p className="font-medium">{formatDate(selectedBooking.scheduledAt)}</p>
                  <p className="text-sm">{selectedBooking.duration} minutes</p>
                </div>
              </div>
              
              {/* Assessment Context */}
              {selectedBooking.assessmentSummary?.length > 0 && (
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Assessment Context</label>
                  <div className="space-y-2">
                    {selectedBooking.assessmentSummary.map((a, idx) => (
                      <div key={idx} className="bg-muted/30 rounded p-3 text-sm">
                        {a.type === 'fear' && (
                          <>
                            <p className="font-medium text-amber-700">EDGE Diagnostic</p>
                            <p>Primary Edge: {a.primaryFear} ({a.intensity})</p>
                          </>
                        )}
                        {a.type === 'archetype' && (
                          <>
                            <p className="font-medium text-violet-700">CORE Diagnostic</p>
                            <p>Stated: {a.primaryArchetype}</p>
                            <p>Core: {a.secondaryArchetype}</p>
                            {a.hasGap && <p className="text-amber-600">⚠️ Stated-Core Gap Detected</p>}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Status Update */}
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Status</label>
                <Select 
                  value={selectedBooking.status}
                  onValueChange={(value) => handleUpdateBooking(selectedBooking.id, { status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="no_show">No Show</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Admin Notes */}
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Admin Notes</label>
                <Textarea 
                  defaultValue={selectedBooking.adminNotes || ''}
                  placeholder="Add notes about this booking..."
                  onBlur={(e) => {
                    if (e.target.value !== selectedBooking.adminNotes) {
                      handleUpdateBooking(selectedBooking.id, { adminNotes: e.target.value });
                    }
                  }}
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Add Booking Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Booking</DialogTitle>
            <DialogDescription>
              Manually create a booking for a participant
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="participantEmail">Participant Email *</Label>
              <Input
                id="participantEmail"
                type="email"
                placeholder="participant@example.com"
                value={newBooking.participantEmail}
                onChange={(e) => setNewBooking({ ...newBooking, participantEmail: e.target.value })}
                data-testid="booking-email-input"
              />
              <p className="text-xs text-muted-foreground">Must be an existing user in the system</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="scheduledAt">Date & Time *</Label>
              <Input
                id="scheduledAt"
                type="datetime-local"
                value={newBooking.scheduledAt}
                onChange={(e) => setNewBooking({ ...newBooking, scheduledAt: e.target.value })}
                data-testid="booking-datetime-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="duration">Duration (minutes)</Label>
                <Select
                  value={String(newBooking.duration)}
                  onValueChange={(value) => setNewBooking({ ...newBooking, duration: parseInt(value) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 min</SelectItem>
                    <SelectItem value="45">45 min</SelectItem>
                    <SelectItem value="60">60 min</SelectItem>
                    <SelectItem value="90">90 min</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="sessionType">Session Type</Label>
                <Select
                  value={newBooking.sessionType}
                  onValueChange={(value) => setNewBooking({ ...newBooking, sessionType: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="facilitated_session">Facilitated Session</SelectItem>
                    <SelectItem value="debrief">Debrief</SelectItem>
                    <SelectItem value="coaching">Coaching</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                placeholder="Any notes about this booking..."
                value={newBooking.notes}
                onChange={(e) => setNewBooking({ ...newBooking, notes: e.target.value })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddBooking} 
              disabled={addingBooking}
              data-testid="create-booking-btn"
            >
              {addingBooking ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Booking
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminBookings;
